import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ViewModeService } from '@services/view-mode.service';

import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { Logger } from '@utils/logger';

/**
 * Logger
 */
const logger = Logger.getLogger('RichTextComponent');

/**
 * Interface for the RichText Content
 */
interface RichTextContent {
  content: RichTextValue;
}

interface RichTextValue {
  value: string;
}

/**
 * FT Rich Text component
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `ft-rich-text`
 *
 * Exported as: `RichTextComponent`
 */
@Component({
  selector: 'ft-rich-text',
  templateUrl: './rich-text.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextComponent extends AbstractBaseComponent implements OnInit {
  /**
   * Url of Image ref
   */
  imageUrl: string;

  /**
   * Constructor
   */
  constructor(private viewModeService: ViewModeService) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Get content from document
   */
  get content() {
    const content = this.document?.getData<RichTextContent>();
    if (content?.content?.value) {
      // Transform footnote links to include scroll functionality
      const transformedValue = this.transformFootnoteLinks(
        content.content.value
      );
      return {
        ...content,
        content: {
          ...content.content,
          value: transformedValue,
        },
      };
    }
    return content;
  }

  /**
   * Transform footnote links to include scroll back functionality
   */
  private transformFootnoteLinks(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const paragraphs = Array.from(doc.querySelectorAll('p'));

    const footnoteRegex = /\[(\d+)\]/g;

    paragraphs.forEach((p) => {
      const small = p.querySelector('small');
      if (!small) {
        return;
      }

      const text = small.textContent?.trim();

      // Case 1: [2] style footnotes
      const bracketMatches = text?.match(footnoteRegex);
      if (bracketMatches) {
        bracketMatches.forEach((footnote) => {
          const footnoteNumber = footnote.replace(/\[|\]/g, '');
          const anchor = `<a href="javascript:void(0)" onclick="document.getElementById('fnref${footnoteNumber}')?.scrollIntoView({ behavior: 'smooth', block: 'center' })" id="fn${footnoteNumber}" class="strategy-data__footnotes-src padding-right--xs">${footnoteNumber}</a>`;
          small.innerHTML = small.innerHTML.replace(footnote, anchor);
        });
      }

      // Case 2: "2. Some text" style footnotes
      const numberedMatch = text?.match(/^(\d+)\.\s+(.*)/);
      if (numberedMatch) {
        const footnoteNumber = numberedMatch[1];
        const rest = numberedMatch[2];
        const anchor = `<a href="javascript:void(0)" onclick="document.getElementById('fnref${footnoteNumber}')?.scrollIntoView({ behavior: 'smooth', block: 'center' })" id="fn${footnoteNumber}" class="strategy-data__footnotes-src padding-right--xs">${footnoteNumber}</a>`;
        small.innerHTML = `${anchor} ${rest}&nbsp;`;
      }
    });

    return doc.body.innerHTML;
  }
}
